// 一个流程控制器，用于控制任务的执行
import uniqid from 'uniqid';
import { getProjectState, setProjectState } from '@starflow/app/store';
import { prepareGenerateParams } from '../modules/generator/helpers';
import { flowCalculatePower } from '@shakkerai/services/src/vip';
import { getVipState } from '@shakkerai/pro-store';
import { notifyWarn } from '@shakkerai/pro-components';
import {
  getProcessor,
  findFlowFirstNodeId,
  findFlowNextNodeId,
  startAutoRunningFlow,
  endAutoRunningFlow,
  getFlowNodeOrderList,
  createFlowFromUnit,
} from './flow';
import { app } from '../index';
import { translate } from '@shakkerai/util-i18n';
import { reportModelUsage } from '../modules/models/store/actions';

export const tryResumeTasks = async () => {
  await new Promise((resolve) => setTimeout(resolve, 0));

  const tasks = app.store.getTasks();
  const { map, current } = tasks || {};

  if (current) {
    const currentTask = map?.[current];

    if (currentTask) {
      if (currentTask.status === 'idle' || currentTask.status === 'error') {
        // 对于闲置或者错误状态的任务，直接清理掉
        app.store.removeTask(map[current].id);
        app.store.updateCurrentTask(null);
      } else if (
        currentTask.hasSubTasks &&
        currentTask.results &&
        currentTask.results.every(
          // 如果所有子任务都是在异常状态，也清理掉
          (item) => item.status === 'pending' || item.status === 'idle' || item.status === 'error',
        )
      ) {
        app.store.removeTask(map[current].id);
        app.store.updateCurrentTask(null);
      } else {
        const processor = getProcessor(map[current].type);

        if (processor && processor.resume) {
          try {
            await processor.resume({ data: map[current], processor, app });
          } catch (e) {
            console.log(`[DEBUG] tryResumeTasks error:`, e);
          } finally {
            app.store.removeTask(map[current].id);
            app.store.updateCurrentTask(null);
          }
        }
      }
    } else {
      app.store.updateCurrentTask(null);
    }
  }

  // 执行下一个任务
  executeNextTask();
};

export const scheduleTaskForUnit = async (unitId, { isAuto = false, isFakeSize = false, extraParams } = {}) => {
  const unit = app.store.getProjectState('nodeMap')[unitId];

  console.log('[DEBUG] ~ scheduleTaskForUnit ~ unit:', unit);

  unit.data?.data?.rawData && reportModelUsage(unit.data.data.rawData);
  if (!unit) {
    // 如果节点不存在，则结束自动运行
    isAuto && endAutoRunningFlow();
    return;
  }

  let { type, label, data: params, flowId } = unit.data;

  delete params?.isDetailsVary;
  delete params?.isOverallVary;

  // 是否是从flow中手动发起的generator任务, 这个场景下，图片不需要展示在画布上，展示在derivative中
  const isFlowManualGenerator =
    !!flowId && !isAuto && type === 'generator' && app.store.getProjectState('currentElementTool') === 'generator';

  if (flowId) {
    const { shouldRebuild, removedNodes } = rebuildFlow(flowId);

    if (shouldRebuild) {
      // 流需要重构
      if (removedNodes[unitId]) {
        // 如果当前节点已经被删除，则结束自动运行
        notifyWarn(translate('节点已失效，流已更新，请重新运行'));
        return;
      } else {
        notifyWarn(translate('流已更新，将会自动运行新的流'));
      }
    }
  } else {
    // 如果不是flow中的节点，还需要检测当前单元是否包含了有效的输入以及是否需要形成流
    const result = await createFlowFromUnit(unitId);

    console.log(`[DEBUG] createFlowFromUnit`, result);

    if (result.newFlowCreated) {
      flowId = result.flowId;
      unitId = result.unitId;
    }
  }

  scheduleTask({
    type,
    isAuto,
    unitId,
    flowId,
    isFakeSize,
    isFlowManualGenerator,
    params: Object.assign({}, params, { unitId, unitLabel: label }, extraParams),
  });
};

export const checkFlowPower = async (flowId) => {
  const userVipInfo = getVipState('userVipInfo');
  const remainedPower = userVipInfo?.attr?.totalPower - userVipInfo?.attr?.usedPower || 0;
  const nodeOrderList = getFlowNodeOrderList(flowId);

  if (!nodeOrderList?.length) {
    return false;
  }

  const nodeMap = getProjectState('nodeMap');

  let allNodeParams = [];

  for (const nodeId of nodeOrderList) {
    const nodeInfo = nodeMap[nodeId];
    const params = await prepareGenerateParams({
      type: nodeInfo?.data?.type,
      params: nodeInfo?.data?.data,
      isTryClacMode: true,
    });
    if (params) {
      allNodeParams.push(params);
    }
  }

  console.log('allNodeParams', allNodeParams);

  const res = await flowCalculatePower(allNodeParams);
  console.log('[DEBUG] ~ checkFlowPower ~ res:', res);

  const consumerPower = res?.power;

  if (!consumerPower || remainedPower < consumerPower) {
    return false;
  }
  return true;
};

export const scheduleTaskForFlow = async (flowId) => {
  const firstNodeId = findFlowFirstNodeId(flowId);
  console.log('[DEBUG] ~ scheduleTaskForFlow ~ firstNodeId:', flowId, firstNodeId);
  // 算力校验
  const flag = await checkFlowPower(flowId);
  if (!flag) {
    return notifyWarn(translate('算力不足，请充值后再试！'));
  }

  if (flowId) {
    const { shouldRebuild } = rebuildFlow(flowId);
    if (shouldRebuild) {
      notifyWarn(translate('流已更新，将会自动运行新的流'));
    }
  }

  // 开始自动运行
  startAutoRunningFlow(flowId, firstNodeId);

  // 只需要找到第一个节点并在运行的时候指定isAuto为true即可
  scheduleTaskForUnit(firstNodeId, { isAuto: true });
};

export const scheduleTask = async (taskData) => {
  if (getProjectState('tasks').fe_queue.length >= 2) {
    return notifyWarn(translate('generator_limit'));
  }

  // 将任务加入任务队列
  taskData.id = `${taskData.type}-${uniqid()}`;

  app.store.appendTaskQueue(taskData);

  await new Promise((resolve) => setTimeout(resolve, 0));

  console.log(`[DEBUG] taskData`, taskData);

  const processor = getProcessor(taskData.type);

  // 目前只有生成器有onQueue
  if (processor.onQueue) {
    processor.onQueue({ data: taskData, processor, app });
  }

  // 执行任务
  executeNextTask();

  // return new Promise((resolve, reject) => {
  //   emitter.once(`task:${taskData.id}:success`, resolve);
  //   emitter.once(`task:${taskData.id}:error`, reject);
  // });
};

export const executeNextTask = async () => {
  const { current, map } = app.store.getTasks();

  // 如果当前有任务正在执行，则不执行下一个任务
  if (current && map[current] && map[current].status !== 'idle' && map[current].status !== 'error') return;

  // 取出下一个任务
  const taskData = app.store.shiftTaskQueue();

  // 任务为空，则结束自动运行, 并重置自动运行状态
  if (!taskData) {
    endAutoRunningFlow();
    return;
  }

  const nextTaskId = taskData.id;

  // 更新进行中的任务
  app.store.updateTask(nextTaskId, { ...taskData, results: [], status: 'pending', progress: 0, beginAt: Date.now() });
  app.store.updateCurrentTask(nextTaskId);

  try {
    // 执行任务
    const processor = getProcessor(taskData.type);
    // 注意这里的result不一定代表实际执行结果
    await processor.execute({ data: taskData, processor, app });

    if (taskData.isAuto) {
      const nextNodeId = findFlowNextNodeId(taskData.flowId, getProjectState('autoRunningFlowNodeId')); // 获取下一个节点

      if (nextNodeId) {
        setProjectState({
          autoRunningFlowNodeId: nextNodeId,
        });
        scheduleTaskForUnit(nextNodeId, { isAuto: true }); // 计划下一个节点的任务
      }
    }

    // 任务执行成功
    app.store.removeTask(nextTaskId);
    app.store.updateCurrentTask(null);

    await new Promise((resolve) => setTimeout(resolve, 100));
    executeNextTask();
  } catch (e) {
    console.log(`[DEBUG] execute task error:`, e);
    // 任务执行失败
    // emitter.emit(`task:${nextTaskId}:error`, e);
    if (taskData.isAuto) {
      app.store.updateTask(nextTaskId, { ...taskData, status: 'error', error: e.message ?? '' });
      notifyWarn('流运行失败, 请稍后重试');
    } else {
      app.store.removeTask(nextTaskId);
      app.store.updateCurrentTask(null);
    }

    await new Promise((resolve) => setTimeout(resolve, 100));
    executeNextTask();
  }
};
